import React from 'react';
import { JSONTree } from 'react-json-tree';
import ReactDOM from 'react-dom';
import { getDateTime } from '@/modules/TCIUtils';

const theme = {
  author: 'wimer hazenberg (http://www.monokai.nl)',
  base00: '#000000',
  base01: '#383830',
  base02: '#49483e',
  base03: '#75715e',
  base04: '#a59f85',
  base05: '#f8f8f2',
  base06: '#f5f4f1',
  base07: '#f9f8f5',
  base08: '#f92672',
  base09: '#fd971f',
  base0A: '#f4bf75',
  base0B: '#a6e22e',
  base0C: '#a1efe4',
  base0D: '#66d9ef',
  base0E: '#ae81ff',
  base0F: '#cc6633',
  scheme: 'monokai',
};

// The scannable columns. `params` and `raw_response` are JSON blobs rendered as trees — they
// flatten into unreadable CSV cells, and `raw_response` was already left out before this.
const EXPORTED_COLUMN_NAMES = ['created_at', 'student', 'endpoint', 'http_method', 'category', 'http_status'];

// AIDEV-NOTE: `endpoint` and `http_method` are null on the rows that matter most — LmsWorker's
// log_custom_event records why a sync was skipped (student_not_linked, rate_limited, and the rest)
// and there was no HTTP call to describe. DataTables has no default for a null cell, so these
// columns need both `defaultContent` and a render that tolerates one.
const dash = (data: string | null) => (data == null || data === '' ? '—' : data);

const jsonCell = (td, cellData, isExpanded) => {
  let value = JSON.parse(cellData?.includes('{') ? cellData : '{}');

  try {
    while (typeof value === 'string') {
      value = value.replaceAll('=>', ':').replaceAll('nil', 'null');
      value = JSON.parse(value);
    }
  } catch {
    value = JSON.parse(cellData?.includes('{') ? cellData : '{}');
  }

  ReactDOM.render(
    <JSONTree
      invertTheme
      data={value}
      shouldExpandNodeInitially={() => isExpanded}
      theme={theme}
    />,
    td,
  );
};

const syncActivityColumns = (isExpanded: boolean) => {
  const columnDefs = [
    {
      name: 'created_at',
      render: data => getDateTime(data),
      targets: 0,
      title: 'Date',
      type: 'date',
      width: '10%',
    },
    {
      name: 'student',
      targets: 1,
      width: '15%',
    },
    {
      className: 'tw-break-all',
      defaultContent: '',
      name: 'endpoint',
      render: dash,
      targets: 2,
      width: '15%',
    },
    {
      defaultContent: '',
      name: 'http_method',
      render: dash,
      targets: 3,
      title: 'Method',
      width: '5%',
    },
    {
      className: 'tw-break-all',
      name: 'category',
      targets: 4,
      width: '10%',
    },
    {
      className: 'tw-break-all',
      createdCell: (td, cellData) => jsonCell(td, cellData, isExpanded),
      name: 'params',
      sortable: false,
      targets: 5,
      width: '25%',
    },
    {
      className: 'tw-break-all',
      createdCell: (td, cellData) => jsonCell(td, cellData, isExpanded),
      name: 'raw_response',
      sortable: false,
      targets: 6,
      title: 'Response',
      width: '30%',
    },
    {
      name: 'http_status',
      sortable: false,
      targets: 7,
      title: 'HTTP Status',
    },
  ];
  const columns = columnDefs.map(c => c.name);
  const columnMapping = columnDefs.map(c => (c.name === 'actions' ? null : c.name));
  // Resolved by name, not written as literal indices: this list used to be a hardcoded
  // [0, 1, 2, 3, 4], so inserting the Method column silently pushed `params` out of the CSV
  // export and pulled `category` in. Adding a column can no longer change what gets exported.
  const exportedColumns = columnDefs
    .map((c, index) => (EXPORTED_COLUMN_NAMES.includes(c.name) ? index : null))
    .filter(index => index !== null);

  return { columnDefs, columnMapping, columns, exportedColumns };
};

export default syncActivityColumns;
